import * as OTPAuth from 'otpauth';
import QRCode from 'qrcode';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { encrypt, decrypt } from './webCryptoService';

// OTPAuth를 사용한 TOTP 인스턴스 생성
const createTOTP = (secret) => {
  return new OTPAuth.TOTP({
    issuer: 'Password Wallet',
    label: 'Password Wallet',
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: secret || OTPAuth.Secret.fromBase32(generateBase32Secret())
  });
};

// Base32 시크릿 생성
const generateBase32Secret = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  const randomBytes = new Uint8Array(20);
  window.crypto.getRandomValues(randomBytes);
  
  for (let i = 0; i < 32; i++) {
    secret += chars[randomBytes[i % 20] % chars.length];
  }
  return secret;
};

/**
 * TOTP (Time-based One-Time Password) 서비스
 * Google Authenticator와 호환되는 2단계 인증 구현
 */
export const totpService = {
  /**
   * 새로운 TOTP 시크릿 생성
   * @returns {string} Base32 인코딩된 시크릿
   */
  generateSecret() {
    // 32자 시크릿 생성 (Google Authenticator 표준)
    return generateBase32Secret();
  },

  /**
   * QR 코드 URL 생성
   * @param {string} email - 사용자 이메일
   * @param {string} secret - TOTP 시크릿
   * @returns {string} otpauth:// URL
   */
  generateKeyUri(email, secret) {
    const totp = createTOTP(OTPAuth.Secret.fromBase32(secret));
    totp.label = email;
    return totp.toString();
  },

  /**
   * QR 코드 이미지 생성
   * @param {string} otpauthUrl - otpauth:// URL
   * @returns {Promise<string>} Data URL 형식의 QR 코드 이미지
   */
  async generateQRCode(otpauthUrl) {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(otpauthUrl, {
        width: 256,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrCodeDataUrl;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('QR 코드 생성 실패:', error.message);
      }
      throw new Error('QR 코드 생성에 실패했습니다');
    }
  },

  /**
   * TOTP 토큰 생성 (테스트용)
   * @param {string} secret - TOTP 시크릿
   * @returns {string} 6자리 TOTP 코드
   */
  generate(secret) {
    const totp = createTOTP(OTPAuth.Secret.fromBase32(secret));
    return totp.generate();
  },

  /**
   * TOTP 토큰 검증
   * @param {string} token - 사용자가 입력한 6자리 코드
   * @param {string} secret - 저장된 TOTP 시크릿
   * @returns {boolean} 검증 성공 여부
   */
  verify(token, secret) {
    const totp = createTOTP(OTPAuth.Secret.fromBase32(secret));
    // window를 1로 설정하여 이전/다음 30초 구간도 허용
    return totp.validate({ token, window: 1 }) !== null;
  },

  /**
   * 백업 코드 생성
   * @param {number} count - 생성할 백업 코드 개수
   * @returns {Array<string>} 백업 코드 배열
   */
  generateBackupCodes(count = 8) {
    const codes = [];
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    for (let i = 0; i < count; i++) {
      let code = '';
      for (let j = 0; j < 8; j++) {
        code += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      // 4자리-4자리 형식으로 포맷
      codes.push(code.slice(0, 4) + '-' + code.slice(4));
    }
    
    return codes;
  },

  /**
   * 사용자의 2FA 설정 저장
   * @param {string} userId - 사용자 UID
   * @param {Object} twoFactorData - 2FA 설정 데이터
   */
  async saveTwoFactorSettings(userId, twoFactorData) {
    try {
      const { secret, backupCodes, enabled } = twoFactorData;
      
      // 시크릿과 백업 코드 암호화
      const encryptedSecret = await encrypt(secret, userId);
      const encryptedBackupCodes = await encrypt(JSON.stringify(backupCodes), userId);
      
      // Firestore에 저장
      await setDoc(doc(db, 'users', userId, 'settings', 'twoFactor'), {
        enabled,
        encryptedSecret,
        encryptedBackupCodes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastUsed: null,
        usedBackupCodes: [] // 사용된 백업 코드 추적
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ 2FA 설정 저장 완료');
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 2FA 설정 저장 실패:', error.message);
      }
      throw new Error('2FA 설정 저장에 실패했습니다');
    }
  },

  /**
   * 사용자의 2FA 설정 조회
   * @param {string} userId - 사용자 UID
   * @returns {Promise<Object|null>} 2FA 설정 또는 null
   */
  async getTwoFactorSettings(userId) {
    try {
      const docRef = doc(db, 'users', userId, 'settings', 'twoFactor');
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const data = docSnap.data();
      
      // 시크릿과 백업 코드 복호화
      let secret = data.secret;
      let backupCodes = data.backupCodes;
      
      // 암호화된 데이터가 있으면 복호화
      if (data.encryptedSecret) {
        try {
          secret = await decrypt(data.encryptedSecret, userId);
          backupCodes = JSON.parse(await decrypt(data.encryptedBackupCodes, userId));
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('복호화 실패:', error.message);
          }
          // 복호화 실패 시 평문 데이터 사용
        }
      }
      
      return {
        enabled: data.enabled,
        secret,
        backupCodes,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        lastUsed: data.lastUsed,
        usedBackupCodes: data.usedBackupCodes || []
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 2FA 설정 조회 실패:', error.message);
      }
      return null;
    }
  },

  /**
   * 2FA 활성화/비활성화
   * @param {string} userId - 사용자 UID
   * @param {boolean} enabled - 활성화 여부
   */
  async toggleTwoFactor(userId, enabled) {
    try {
      const docRef = doc(db, 'users', userId, 'settings', 'twoFactor');
      
      if (!enabled) {
        // 비활성화 시 문서 삭제
        await setDoc(docRef, {
          enabled: false,
          updatedAt: new Date().toISOString(),
          encryptedSecret: null,
          encryptedBackupCodes: null
        });
      } else {
        await updateDoc(docRef, {
          enabled,
          updatedAt: new Date().toISOString()
        });
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ 2FA ${enabled ? '활성화' : '비활성화'} 완료`);
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 2FA 상태 변경 실패:', error.message);
      }
      throw new Error('2FA 상태 변경에 실패했습니다');
    }
  },

  /**
   * 백업 코드 사용 처리
   * @param {string} userId - 사용자 UID
   * @param {string} code - 사용할 백업 코드
   * @returns {Promise<boolean>} 사용 성공 여부
   */
  async useBackupCode(userId, code) {
    try {
      const settings = await this.getTwoFactorSettings(userId);
      if (!settings) return false;
      
      // 이미 사용된 코드인지 확인
      if (settings.usedBackupCodes.includes(code)) {
        return false;
      }
      
      // 유효한 백업 코드인지 확인
      if (!settings.backupCodes.includes(code)) {
        return false;
      }
      
      // 사용된 코드로 표시
      await updateDoc(doc(db, 'users', userId, 'settings', 'twoFactor'), {
        usedBackupCodes: [...settings.usedBackupCodes, code],
        lastUsed: new Date().toISOString()
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ 백업 코드 사용 완료');
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 백업 코드 사용 실패:', error.message);
      }
      return false;
    }
  },

  /**
   * 시간 남은 시간 계산 (UI 표시용)
   * @returns {number} 다음 코드까지 남은 초
   */
  getTimeRemaining() {
    const totp = createTOTP();
    return totp.period - (Math.floor(Date.now() / 1000) % totp.period);
  },

  /**
   * 현재 시간 단계 (UI 표시용)
   * @returns {number} 현재 시간 단계 (0-29)
   */
  getTimeUsed() {
    const totp = createTOTP();
    return Math.floor(Date.now() / 1000) % totp.period;
  },

  /**
   * 2FA 비활성화
   * @param {string} userId - 사용자 UID
   */
  async disableTwoFactor(userId) {
    try {
      const docRef = doc(db, 'users', userId, 'settings', 'twoFactor');
      await setDoc(docRef, {
        enabled: false,
        encryptedSecret: null,
        encryptedBackupCodes: null,
        updatedAt: new Date().toISOString()
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ 2FA 비활성화 완료');
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 2FA 비활성화 실패:', error.message);
      }
      throw new Error('2FA 비활성화에 실패했습니다');
    }
  },

  /**
   * 백업 코드 업데이트
   * @param {string} userId - 사용자 UID
   * @param {Array<string>} newBackupCodes - 새로운 백업 코드들
   */
  async updateBackupCodes(userId, newBackupCodes) {
    try {
      const encryptedBackupCodes = await encrypt(JSON.stringify(newBackupCodes), userId);
      
      await updateDoc(doc(db, 'users', userId, 'settings', 'twoFactor'), {
        encryptedBackupCodes,
        usedBackupCodes: [], // 사용된 코드 목록 초기화
        updatedAt: new Date().toISOString()
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ 백업 코드 업데이트 완료');
      }
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ 백업 코드 업데이트 실패:', error.message);
      }
      throw new Error('백업 코드 업데이트에 실패했습니다');
    }
  }
};